// src/views/parent/PABulletins.jsx
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useParentDrawer } from "../../context/ParentDrawerContext";

// ─── HELPERS ──────────────────────────────────────────────────────────────
function formatMoyenne(valeur) {
  if (valeur === null || valeur === undefined) return "-";
  const n = parseFloat(valeur);
  return isNaN(n) ? "-" : n.toFixed(2);
}

function getMoyenneColor(moyenne) {
  if (moyenne === null || isNaN(moyenne)) return "#6b7280";
  if (moyenne >= 8) return "#4CAF50";
  if (moyenne >= 6) return "#FF9800";
  return "#F44336";
}

function getMoyenneBg(moyenne) {
  if (moyenne === null || isNaN(moyenne)) return "#f3f4f6";
  if (moyenne >= 8) return "#E8F5E9";
  if (moyenne >= 6) return "#FFF3E0";
  return "#FFEBEE";
}

function getInitiales(nom) {
  const parts = nom.trim().split(" ");
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return nom ? nom[0].toUpperCase() : "?";
}

// ─── SKELETON LOADER ──────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ background: "var(--s2)", minHeight: "100dvh" }}>
      <div style={{
        height: 230, borderRadius: "0 0 40px 40px",
        background: "linear-gradient(135deg, var(--pr-bg), var(--pr-lt))",
        animation: "skPulse 1.2s ease-in-out infinite alternate",
      }} />
      <div style={{ padding: 16, marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            height: 130, borderRadius: 20, background: "var(--b2)",
            animation: "skPulse 1.2s ease-in-out infinite alternate",
            animationDelay: `${i * 0.15}s`,
          }} />
        ))}
      </div>
    </div>
  );
}

// ─── HEADER ───────────────────────────────────────────────────────────────
function Header({ openDrawer }) {
  return (
    <div style={{ position: "relative" }}>
      <div style={{
        padding: "52px 16px 40px",
        background: "linear-gradient(135deg, var(--pr-dk), var(--pr))",
        borderRadius: "0 0 40px 40px",
        boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
        position: "relative", overflow: "hidden",
      }}>
        <i className="ti ti-file-text" style={{ color: "#fff", fontSize: 60 }} />
        <div style={{
          fontFamily: "Poppins, sans-serif", color: "#fff",
          fontSize: 24, fontWeight: 700, textAlign: "center",
        }}>
          Bulletins scolaires
        </div>
        <div style={{
          fontFamily: "Inter, sans-serif", color: "rgba(255,255,255,0.7)",
          fontSize: 14, textAlign: "center",
        }}>
          Consultez les résultats académiques
        </div>

        <div style={{
          position: "absolute", left: "20%", right: "20%", bottom: 0,
          height: 3,
          background: "linear-gradient(90deg, transparent, var(--or), transparent)",
        }} />
      </div>

      <button
        onClick={openDrawer}
        aria-label="Ouvrir le menu"
        style={{
          position: "absolute", top: 44, left: 16,
          background: "rgba(255,255,255,0.2)", border: "none",
          borderRadius: 12, width: 42, height: 42, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <i className="ti ti-menu-2" style={{ color: "#fff", fontSize: 26 }} />
      </button>
    </div>
  );
}

// ─── STATS SUMMARY ────────────────────────────────────────────────────────
function StatsSummary({ nbEnfants, nbBulletins }) {
  return (
    <div style={{
      margin: 16, padding: 20, background: "#fff", borderRadius: 20,
      boxShadow: "0 4px 10px rgba(0,0,0,0.05)", display: "flex", alignItems: "center",
    }}>
      <div style={{ flex: 1, textAlign: "center" }}>
        <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 28, fontWeight: 700, color: "var(--pr)" }}>
          {nbEnfants}
        </div>
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--m)", marginTop: 4 }}>
          Élèves
        </div>
      </div>
      <div style={{ width: 1, height: 40, background: "var(--b)" }} />
      <div style={{ flex: 1, textAlign: "center" }}>
        <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 28, fontWeight: 700, color: "var(--pr)" }}>
          {nbBulletins}
        </div>
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--m)", marginTop: 4 }}>
          Bulletins
        </div>
      </div>
    </div>
  );
}

// ─── CARTE BULLETIN ───────────────────────────────────────────────────────
function BulletinCard({ bulletin, navigate, slug }) {
  const moyenne = bulletin.moyenne_generale ? parseFloat(bulletin.moyenne_generale) : null;
  const noteMax = bulletin.note_maximale || 10;

  return (
    <div
      onClick={() => navigate(`/${slug}/parent/bulletin-detail/${bulletin.bulletin_id}`, { state: { bulletin } })}
      style={{
        background: "#fff", borderRadius: 16, overflow: "hidden",
        border: "1px solid var(--b)", cursor: "pointer", marginBottom: 12,
      }}
    >
      <div style={{
        padding: "10px 14px", background: "var(--pr)", borderRadius: "15px 15px 0 0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span style={{ fontFamily: "Inter, sans-serif", color: "#fff", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
          <i className="ti ti-sparkles" style={{ fontSize: 14, color: "var(--or-lt)" }} />
          {bulletin.trimestre_nom || ""}
        </span>
        <span style={{
          padding: "3px 8px", borderRadius: 12, background: "rgba(255,255,255,0.15)",
          fontFamily: "Inter, sans-serif", color: "rgba(255,255,255,0.7)", fontSize: 10,
          display: "flex", alignItems: "center", gap: 4,
        }}>
          <i className="ti ti-check-circle" style={{ fontSize: 12 }} />
          Reçu
        </span>
      </div>

      <div style={{ padding: 16, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 500, color: "var(--t)", marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
            <i className="ti ti-category" style={{ fontSize: 14, color: "var(--h)" }} />
            {bulletin.classe_nom || "-"}
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--m)" }}>
            {bulletin.annee_scolaire || ""}
          </div>
        </div>

        <div style={{
          width: 80, height: 80, borderRadius: "50%", flexShrink: 0,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: getMoyenneBg(moyenne),
          border: `2px solid ${getMoyenneColor(moyenne)}4D`,
        }}>
          <span style={{ fontFamily: "Poppins, sans-serif", fontSize: 22, fontWeight: 700, color: getMoyenneColor(moyenne) }}>
            {formatMoyenne(bulletin.moyenne_generale)}
          </span>
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 10, color: "var(--m)" }}>/{noteMax}</span>
        </div>
      </div>
    </div>
  );
}

// ─── SECTION ENFANT ───────────────────────────────────────────────────────
function EnfantSection({ nom, bulletins, navigate, slug }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 20, marginBottom: 24, overflow: "hidden",
      boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
    }}>
      <div style={{
        padding: 20,
        background: "linear-gradient(135deg, rgba(27,79,128,0.05), rgba(192,138,46,0.02))",
        display: "flex", alignItems: "center", gap: 15,
      }}>
        <div style={{
          width: 55, height: 55, borderRadius: "50%",
          background: "linear-gradient(135deg, var(--pr), var(--or))",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <span style={{ fontFamily: "Poppins, sans-serif", color: "#fff", fontWeight: 700, fontSize: 18 }}>
            {getInitiales(nom)}
          </span>
        </div>
        <div>
          <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--t)" }}>
            {nom}
          </div>
          <div style={{
            display: "inline-flex", marginTop: 6,
            padding: "4px 10px", borderRadius: 12, background: "rgba(192,138,46,0.1)",
            fontFamily: "Inter, sans-serif", fontSize: 11, fontWeight: 600, color: "var(--or-dk)",
          }}>
            {bulletins.length} bulletin{bulletins.length > 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {bulletins.map((b) => (
          <BulletinCard key={b.bulletin_id} bulletin={b} navigate={navigate} slug={slug} />
        ))}
      </div>
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────
function BulletinsStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');

      *, *::before, *::after { box-sizing: border-box; }

      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 4px; }

      @keyframes skPulse {
        from { opacity: 0.55; }
        to { opacity: 1; }
      }

      .pa-bulletins-error {
        background: #fff;
        border-radius: 20px;
        padding: 40px 20px;
        text-align: center;
        box-shadow: 0 4px 18px rgba(0,0,0,0.08);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        margin: 16px;
      }
      .pa-bulletins-error i.alert-icon {
        font-size: 48px;
        color: var(--rd);
      }
      .pa-bulletins-error h2 {
        font-family: 'Poppins', sans-serif;
        font-size: 17px; font-weight: 600;
        color: var(--t); margin: 4px 0 0;
      }
      .pa-bulletins-error p {
        font-family: 'Inter', sans-serif;
        font-size: 13px; color: var(--m);
        margin: 0 0 12px;
      }
      .pa-bulletins-error .actions-row {
        display: flex; gap: 8px;
        flex-wrap: wrap; justify-content: center;
      }
      .pa-bulletins-error .btn {
        border: none; border-radius: 30px;
        padding: 10px 22px; cursor: pointer;
        font-family: 'Inter', sans-serif;
        font-size: 13px; font-weight: 600;
        color: #fff; background: var(--pr);
        display: inline-flex; align-items: center; gap: 6px;
        transition: opacity 0.2s;
      }
      .pa-bulletins-error .btn:hover { opacity: 0.9; }
      .pa-bulletins-error .btn.btn-secondary {
        background: var(--b2); color: var(--t);
      }
    `}</style>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────
export default function PABulletins({ slug }) {
  const navigate = useNavigate();
  const { getBulletinsParent, getEnfants } = useParentService();
  const { openDrawer } = useParentDrawer();

  const [tousLesBulletins, setTousLesBulletins] = useState([]);
  const [enfants, setEnfants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT DES DONNÉES ─────────────────────────────────────────
  // Dépendance UNIQUEMENT sur slug → identité stable (anti-boucle)
  const loadData = useCallback(async () => {
    if (!slug) {
      setError("Identifiant parent manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [enfantsData, bulletinsData] = await Promise.all([
        getEnfants(slug),
        getBulletinsParent(slug),
      ]);

      setEnfants(enfantsData || []);
      setTousLesBulletins(bulletinsData || []);
    } catch (err) {
      setError("Impossible de récupérer les bulletins.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadData();
  }, [loadData]);

  // ─── GROUPEMENT PAR ENFANT ──────────────────────────────────────────
  const groupes = useMemo(() => {
    const map = new Map();
    for (const b of tousLesBulletins) {
      const key = `${b.eleve_prenom ?? ""} ${b.eleve_nom ?? ""}`.trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    }
    return Array.from(map.entries());
  }, [tousLesBulletins]);

  // ─── RENDU ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <BulletinsStyles />
        <SkeletonLoader />
      </>
    );
  }

  return (
    <>
      <BulletinsStyles />

      <div style={{ background: "var(--s2)", minHeight: "100vh" }}>
        {/* Header TOUJOURS affiché (menu accessible) */}
        <Header openDrawer={openDrawer} />

        {error ? (
          /* ── ÉTAT ERREUR (header conservé au-dessus) ── */
          <div className="pa-bulletins-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button className="btn" onClick={loadData}>
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/${slug}/parent/accueil`)}
              >
                <i className="ti ti-home" />
                Accueil
              </button>
            </div>
          </div>
        ) : enfants.length === 0 || tousLesBulletins.length === 0 ? (
          /* ── ÉTAT VIDE ── */
          <div style={{
            margin: 32, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
          }}>
            <div style={{
              width: 80, height: 80, borderRadius: "50%", background: "rgba(27,79,128,0.1)",
              display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20,
            }}>
              <i className="ti ti-file-text" style={{ fontSize: 40, color: "var(--pr)" }} />
            </div>
            <div style={{ fontFamily: "Poppins, sans-serif", fontSize: 17, fontWeight: 600, color: "var(--t)" }}>
              Aucun bulletin disponible
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--m)", marginTop: 8 }}>
              Les bulletins apparaîtront ici une fois générés par l'école.
            </div>
          </div>
        ) : (
          /* ── CONTENU ── */
          <>
            <StatsSummary nbEnfants={groupes.length} nbBulletins={tousLesBulletins.length} />
            <div style={{ padding: "0 16px 30px" }}>
              {groupes.map(([nom, bulletins]) => (
                <EnfantSection key={nom} nom={nom} bulletins={bulletins} navigate={navigate} slug={slug} />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}